import type { ReactNode } from 'react';
import { ShieldOff } from 'lucide-react';
import { usePermission } from '@hooks/usePermission';

interface PageGuardProps {
  /** Permission code required to view this page */
  permission: string;
  /** Optional business scope */
  businessId?: string;
  children: ReactNode;
}

/**
 * Wraps a page: if the user lacks the required permission, shows "Access Denied"
 * instead of the page content. Security is enforced by the backend; this only
 * controls what the user sees.
 */
export function PageGuard({ permission, businessId, children }: PageGuardProps) {
  const { hasPermission } = usePermission();

  if (!hasPermission(permission, businessId)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <div className="w-16 h-16 rounded-2xl bg-danger-50 flex items-center justify-center mb-4">
          <ShieldOff className="w-8 h-8 text-danger-400" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-sm text-gray-500 max-w-sm">
          You don't have permission to view this page. Contact your administrator if you think this is a mistake.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
